import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentMethodTabs = ({ onPaymentSubmit, isProcessing }) => {
  const [activeTab, setActiveTab] = useState('card');
  const [cardData, setCardData] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: ''
  });
  const [upiId, setUpiId] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedWallet, setSelectedWallet] = useState('');

  const tabs = [
    { id: 'card', label: 'Credit/Debit Card', icon: 'CreditCard' },
    { id: 'upi', label: 'UPI', icon: 'Smartphone' },
    { id: 'netbanking', label: 'Net Banking', icon: 'Building2' },
    { id: 'wallet', label: 'Wallets', icon: 'Wallet' }
  ];

  const banks = [
    { value: 'hdfc', label: 'HDFC Bank' },
    { value: 'icici', label: 'ICICI Bank' },
    { value: 'sbi', label: 'State Bank of India' },
    { value: 'axis', label: 'Axis Bank' },
    { value: 'kotak', label: 'Kotak Mahindra Bank' }
  ];

  const wallets = [
    { value: 'paytm', label: 'Paytm' },
    { value: 'phonepe', label: 'PhonePe' },
    { value: 'googlepay', label: 'Google Pay' },
    { value: 'amazonpay', label: 'Amazon Pay' }
  ];

  const handleCardSubmit = (e) => {
    e?.preventDefault();
    onPaymentSubmit({ method: 'card', data: cardData });
  };

  const handleUpiSubmit = (e) => {
    e?.preventDefault();
    onPaymentSubmit({ method: 'upi', data: { upiId } });
  };

  const handleNetBankingSubmit = (e) => {
    e?.preventDefault();
    onPaymentSubmit({ method: 'netbanking', data: { bank: selectedBank } });
  };

  const handleWalletSubmit = (e) => {
    e?.preventDefault();
    onPaymentSubmit({ method: 'wallet', data: { wallet: selectedWallet } });
  };

  const formatCardNumber = (value) => {
    const cleaned = value?.replace(/\s/g, '');
    const formatted = cleaned?.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted?.substring(0, 19);
  };

  const formatExpiry = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    if (cleaned?.length >= 2) {
      return cleaned?.substring(0, 2) + '/' + cleaned?.substring(2, 4);
    }
    return cleaned;
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-all border-b-2 ${
              activeTab === tab?.id
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={18} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Card Payment Form */}
      {activeTab === 'card' && (
        <form onSubmit={handleCardSubmit} className="space-y-4">
          <Input
            label="Card Number"
            placeholder="1234 5678 9012 3456"
            value={cardData?.number}
            onChange={(e) => setCardData({ ...cardData, number: formatCardNumber(e?.target?.value) })}
            required
          />
          <Input
            label="Cardholder Name"
            placeholder="John Doe"
            value={cardData?.name}
            onChange={(e) => setCardData({ ...cardData, name: e?.target?.value })}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Expiry Date"
              placeholder="MM/YY"
              value={cardData?.expiry}
              onChange={(e) => setCardData({ ...cardData, expiry: formatExpiry(e?.target?.value) })}
              required
            />
            <Input
              label="CVV"
              placeholder="123"
              type="password"
              maxLength={3}
              value={cardData?.cvv}
              onChange={(e) => setCardData({ ...cardData, cvv: e?.target?.value?.replace(/\D/g, '') })}
              required
            />
          </div>
          <div className="flex items-start gap-2 bg-accent/10 rounded-lg p-3">
            <Icon name="Lock" size={16} className="text-accent mt-0.5 flex-shrink-0" />
            <p className="text-sm text-foreground">
              Your card details are encrypted and secure. We never store your CVV.
            </p>
          </div>
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            iconName="Lock"
            loading={isProcessing}
          >
            {isProcessing ? 'Processing Payment...' : 'Complete Purchase'}
          </Button>
        </form>
      )}
      {/* UPI Payment Form */}
      {activeTab === 'upi' && (
        <form onSubmit={handleUpiSubmit} className="space-y-4">
          <Input
            label="UPI ID"
            placeholder="yourname@upi"
            value={upiId}
            onChange={(e) => setUpiId(e?.target?.value)}
            description="Enter your UPI ID to complete payment"
            required
          />
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm font-medium text-foreground mb-3">Or scan QR code</p>
            <div className="bg-white rounded-lg p-4 inline-block">
              <div className="w-48 h-48 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center">
                <Icon name="QrCode" size={80} className="text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Scan with any UPI app (PhonePe, Google Pay, Paytm, etc.)
            </p>
          </div>
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            iconName="Smartphone"
            loading={isProcessing}
          >
            {isProcessing ? 'Processing Payment...' : 'Pay with UPI'}
          </Button>
        </form>
      )}
      {/* Net Banking Form */}
      {activeTab === 'netbanking' && (
        <form onSubmit={handleNetBankingSubmit} className="space-y-4">
          <Select
            label="Select Your Bank"
            placeholder="Choose your bank"
            options={banks}
            value={selectedBank}
            onChange={setSelectedBank}
            searchable
            required
          />
          <div className="bg-accent/10 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">You will be redirected</p>
                <p className="text-muted-foreground">
                  After clicking continue, you'll be redirected to your bank's secure login page.
                </p>
              </div>
            </div>
          </div>
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            iconName="Building2"
            loading={isProcessing}
            disabled={!selectedBank}
          >
            {isProcessing ? 'Redirecting...' : 'Continue to Bank'}
          </Button>
        </form>
      )}
      {/* Wallet Payment Form */}
      {activeTab === 'wallet' && (
        <form onSubmit={handleWalletSubmit} className="space-y-4">
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Select Wallet</label>
            <div className="grid grid-cols-2 gap-3">
              {wallets?.map((wallet) => (
                <button
                  key={wallet?.value}
                  type="button"
                  onClick={() => setSelectedWallet(wallet?.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedWallet === wallet?.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <Icon name="Wallet" size={20} className="text-primary" />
                    </div>
                    <span className="font-medium text-foreground">{wallet?.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            iconName="Wallet"
            loading={isProcessing}
            disabled={!selectedWallet}
          >
            {isProcessing ? 'Processing Payment...' : 'Pay with Wallet'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default PaymentMethodTabs;